import { useState, useEffect } from "react";

interface useCountUpProps {
  value: number | string;
  duration?: number;
}

export const useCountUp = ({ value, duration = 1500 }: useCountUpProps) => {
  const [count, setCount] = useState(0);

  const stringValue = String(value);
  const match = stringValue.match(/([+-]?[0-9.]+)/);
  const numValue = match ? parseFloat(match[1]) : NaN;

  // Extract correct prefix and suffix around the number
  const prefix = match ? stringValue.substring(0, match.index) : "";
  const suffix = match ? stringValue.substring(match.index! + match[1].length) : stringValue;

  useEffect(() => {
    if (isNaN(numValue)) {
      setCount(0);
      return;
    }

    let start = 0;
    const end = numValue;
    const startTime = performance.now();

    const animateNumber = (currentTime: number) => {
      const elapsedTime = currentTime - startTime;
      const progress = Math.min(elapsedTime / duration, 1);

      // Easing outExpo
      const easing = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      const currentCount = start + (end - start) * easing;

      setCount(currentCount);

      if (progress < 1) {
        requestAnimationFrame(animateNumber);
      } else {
        setCount(end);
      }
    };

    const animationId = requestAnimationFrame(animateNumber);
    return () => cancelAnimationFrame(animationId);
  }, [numValue, duration]);

  const isInteger = numValue % 1 === 0;
  // Use toFixed to preserve decimal layout, rounding to match integer input
  const displayCount = isInteger ? Math.round(count).toString() : count.toFixed(2);

  return {
    displayCount,
    prefix,
    suffix,
    isNaNValue: isNaN(numValue)
  };
};

export default useCountUp;
